import React, { useMemo, useState } from 'react';

import Drawer from '../../../../../components/shared/Drawer';
import styled from 'styled-components';

import aampeTheme from '../../../../../constants/aampeTheme';
import ICONS from '../../../../../constants/icons';

import useGetFormulaCoverage from '../../../../../hooks/personalization/useGetFormulaCoverage';

import { FormulaCoverageMap } from 'models/personalization.model';

import {
    FormulaCoverageClassification,
    SelectedCellLabels,
} from '../../contants';
import DrawerHeader from './DrawerHeader';
import FormulaCoverageTable from './FormulaCoverageTable';

export type ContentCoverageDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    xAxisComponent: string;
    yAxisComponent: string;
    setSelectedCell: (value: SelectedCellLabels) => void;
    selectedCellLabels: SelectedCellLabels;
    filters?: Record<string, string[]>;
};

const ContentCoverageDrawer: React.FC<ContentCoverageDrawerProps> = ({
    isOpen,
    onClose,
    xAxisComponent,
    yAxisComponent,
    setSelectedCell,
    selectedCellLabels,
    filters,
}) => {
    const [isOpportunitiesInfoVisible, setIsOpportunitiesInfoVisible] =
        useState(true);

    const { data, isLoading } = useGetFormulaCoverage(
        xAxisComponent,
        yAxisComponent,
        selectedCellLabels?.xAxisSelectedLabel?.value as number,
        selectedCellLabels?.yAxisSelectedLabel?.value as number,
        filters,
    );

    const formulaCoveragesMessages =
        useMemo<FormulaCoverageClassification>(() => {
            const am: FormulaCoverageMap[] = [];
            const lm: FormulaCoverageMap[] = [];
            const hm: FormulaCoverageMap[] = [];
            data?.forEach((item) => {
                if (item.labels.every((label) => label.label_count == 0)) {
                    hm.push(item);
                } else if (
                    item.labels.some((label) => label.label_count == 0)
                ) {
                    lm.push(item);
                } else {
                    am.push(item);
                }
            });
            return {
                lowOpportunityMessages: lm,
                associatedMessages: am,
                highOpportunityMessages: hm,
            };
        }, [data]);

    const opportunitiesMessages = useMemo(
        () => [
            ...formulaCoveragesMessages.lowOpportunityMessages,
            ...formulaCoveragesMessages.highOpportunityMessages,
        ],
        [formulaCoveragesMessages],
    );

    return (
        <Drawer
            width="720px"
            onClose={onClose}
            isOpen={isOpen}
            showCloseButton={false}
            showOverlay={true}
        >
            <DrawerHeader
                selectedCellLabels={selectedCellLabels}
                onClose={onClose}
                xAxisComponent={xAxisComponent}
                yAxisComponent={yAxisComponent}
                setSelectedCell={setSelectedCell}
            />
            <ScrollableDrawerContainer>
                {opportunitiesMessages.length > 0 &&
                    isOpportunitiesInfoVisible && (
                        <OpportunitiesInfoBanner>
                            <BannerTop>
                                <BannerTitleRow>
                                    <BannerInfoIconWrap>
                                        <BannerInfoIcon size={12} />
                                    </BannerInfoIconWrap>
                                    <BannerHeading>
                                        Add alternates to existing messages or
                                        create new ones
                                    </BannerHeading>
                                </BannerTitleRow>
                                <BannerClose
                                    size={18}
                                    onClick={() =>
                                        setIsOpportunitiesInfoVisible(false)
                                    }
                                />
                            </BannerTop>
                            <BannerDescription>
                                These Messages already contain both components,
                                but are missing one or both of the selected
                                labels. Adding alternates here is a quick way to
                                expand content coverage and agent learning.
                            </BannerDescription>
                        </OpportunitiesInfoBanner>
                    )}
                {formulaCoveragesMessages.associatedMessages.length > 0 && (
                    <FormulaCoverageTable
                        key={`associated-${selectedCellLabels?.xAxisSelectedLabel?.value}-${selectedCellLabels?.yAxisSelectedLabel?.value}`}
                        data={formulaCoveragesMessages.associatedMessages}
                        title="Associated Messages"
                        isLoading={isLoading}
                        tableName="associated_messages_table"
                    />
                )}
                <FormulaCoverageTable
                    key={`opportunities-${selectedCellLabels?.xAxisSelectedLabel?.value}-${selectedCellLabels?.yAxisSelectedLabel?.value}`}
                    data={opportunitiesMessages}
                    showAddMessageButton
                    title="Opportunities"
                    isLoading={isLoading}
                    tableName="opportunities_messages_table"
                />
            </ScrollableDrawerContainer>
        </Drawer>
    );
};

const ScrollableDrawerContainer = styled.div({
    height: '100vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
});

const OpportunitiesInfoBanner = styled.div({
    border: `1px solid ${aampeTheme.colors.solidA02}`,
    borderRadius: aampeTheme.borderRadius.default,
    margin: aampeTheme.spacing4(5, 5, 0, 5),
    padding: aampeTheme.spacing4(3, 4),
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
    backgroundColor: aampeTheme.colors.backgroundWG01,
});

const BannerTop = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: aampeTheme.spacing4(2),
});

const BannerTitleRow = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
});

const BannerHeading = styled.div({
    color: aampeTheme.colors.solidA02,
    fontSize: aampeTheme.size.font.text,
    fontWeight: aampeTheme.weights.medium,
    lineHeight: 1.3,
});

const BannerDescription = styled.div({
    color: aampeTheme.colors.textAccessibleWG01,
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.light,
    lineHeight: 1.4,
});

const BannerClose = styled(ICONS.close)({
    color: aampeTheme.colors.textAccessibleWG01,
    cursor: 'pointer',
    flexShrink: 0,
});

const BannerInfoIconWrap = styled.span({
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: aampeTheme.colors.solidA02,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
});

const BannerInfoIcon = styled(ICONS.info)({
    color: aampeTheme.colors.white,
    flexShrink: 0,
});
export default ContentCoverageDrawer;
